<template>
  <div class="message-tab-container">
    <!-- Header with title and important toggle -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <span class="font-weight-bold">
        {{ $t("communication.comMessage.newMessage") }}
      </span>
      <q-toggle
        v-model="importantTag"
        color="red"
        class="tw-custom-toggle"
        :label="$t('communication.comMessage.important') || 'Important'"
      />
    </div>

    <div class="row form-group">
      <!-- Subject -->
      <div class="col-lg-12 col-md-12 col-sm-12 form-control no-border">
        <label for="message-tab-subject" class="d-block mb-0 small text-muted">
          {{ $t("communication.comMessage.subjectLabel") }}
        </label>
        <searchable-select
          input-id="message-tab-subject"
          outlined
          bottom-slots
          v-model="subject"
          :options="subjectOptions"
          :rules="[isRequired]"
          dense
          options-dense
        >
          <template v-slot:append>
            <q-icon
              v-if="subject"
              name="cancel"
              size="xs"
              class="cursor-pointer"
              @click.stop.prevent
              @click="subject = null"
            />
          </template>
        </searchable-select>
      </div>

      <!-- Reference -->
      <div class="col-lg-12 col-md-12 col-sm-12 form-control no-border">
        <label for="message-tab-reference" class="d-block mb-0 small text-muted">
          {{ $t("communication.comMessage.referenceLabel") }}
        </label>
        <q-select
          input-id="message-tab-reference"
          outlined
          use-input
          multiple
          v-model="selectedReference"
          :options="referenceOptions"
          @filter="filterRefFn"
          @click.stop.prevent="abortRefFilter"
          dense
          options-dense
          emit-value
          map-options
        >
          <template v-slot:selected-item="scope">
            <q-chip
              removable
              dense
              @remove="scope.removeAtIndex(scope.index)"
              :tabindex="scope.tabindex"
              color="white"
              text-color="primary"
              class="badge badge-primary d-flex"
              @click.stop.prevent="abortRefFilter"
              v-if="getSelectedLabel(scope) && getSelectedLabel(scope).context && getSelectedLabel(scope).id"
            >
              <span class="mr-2 font-weight-normal">{{
                getSelectedLabel(scope) && getSelectedLabel(scope).context
                  ? $t("communication.comMessage.".concat(getSelectedLabel(scope).context))
                  : ''
              }}</span>
              -
              {{ getSelectedLabel(scope) && getSelectedLabel(scope).id ? getSelectedLabel(scope).id : '' }}
            </q-chip>
          </template>
          <template v-slot:option="scope">
            <div class="theme-1" v-if="getSelectedLabel(scope) && getSelectedLabel(scope).context && getSelectedLabel(scope).id">
              <div
                class="border p-2"
                :key="getSelectedLabel(scope)"
                clickable
                v-ripple
                v-close-popup
                v-bind="scope.itemProps"
              >
                <div class="d-flex align-items-center">
                  <span class="badge badge-primary">
                    {{ getSelectedLabel(scope) && getSelectedLabel(scope).context
                      ? $t("communication.comMessage.".concat(getSelectedLabel(scope).context))
                      : ''
                    }}
                  </span>
                  <span class="ml-2">
                    {{ getSelectedLabel(scope) && getSelectedLabel(scope).id ? getSelectedLabel(scope).id : '' }}
                  </span>
                </div>
              </div>
            </div>
          </template>
          <template v-slot:no-option>
            <q-item>
              <q-item-section class="text-grey">
                {{ $t("main.noResults") }}
              </q-item-section>
            </q-item>
          </template>
        </q-select>
      </div>

      <!-- Recipients -->
      <combo-recipients 
        ref="comboRecipients" 
        :recipients="recipientOptions" 
        :metadata="selectedReference" 
      />

      <!-- Message Body -->
      <div class="col-lg-12 col-md-12 col-sm-12 form-control no-border">
        <label for="message-tab-body" class="d-block mb-0 small text-muted">
          {{ $t("communication.comMessage.messageLabel") || "Message" }}
        </label>
        <q-input 
          input-id="message-tab-body"
          data-testid="textarea_testid" 
          v-model="message" 
          outlined 
          type="textarea" 
          :rows="4"
        />
      </div>
    </div>

    <!-- Loading indicator -->
    <q-inner-loading
      :showing="isSending"
      :label="$t('communication.comMessage.waitSendMessageLabel')"
      label-class="text-blue"
      label-style="font-size: 1.1em"
    />

    <!-- Footer with buttons -->
    <div class="d-flex justify-content-end align-items-center mt-3" style="gap: 10px">
      <q-btn
        class="btn btn-primary w-auto"
        flat
        push
        :label="$t('communication.comMessage.sendBtn')"
        type="submit"
        @click="sendMessage"
      />
    </div>
  </div>
</template>

<script lang="ts">
import store from "@/store";
import { Options, Vue } from "vue-class-component";
import { mapActions } from "vuex";
import i18n from "@/i18n";
import ComboRecipients from "@/components/communication/comboRecipients.vue";
import { convertToUON } from "@/utils";
import {
  clientAppMsg,
  messageTemplate,
  objectType_odmTransferReplyMsg,
  recipient,
  senderUid,
  subject,
  typeMsg,
} from "@/types";

const systemUid = `odm-contactmechanism`;

@Options({
  name: "MessageTab",
  components: {
    ComboRecipients,
  },
  props: {
    subjects: {
      type: Array,
      default: () => [],
    },
    references: {
      type: Array,
      default: () => [],
    },
    reference: {
      type: Array,
      default: () => null,
    },
    recipients: {
      type: Array,
      default: () => [],
    },
  },
  watch: {
    references: {
      handler(newVal: any) {
        this.referenceOptions = newVal || [];
      },
      immediate: true,
    },
    reference: {
      handler(newVal: any) {
        this.selectedReference = newVal;
      },
      immediate: true,
    },
    subjects: {
      handler(newVal: any) {
        this.subjectOptions = newVal || [];
      },
      immediate: true,
    },
    recipients: {
      handler(newVal: any) {
        this.recipientOptions = newVal || [];
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions("messagingModule", ["messageSend", "fetchRecipients"]),

    async sendMessage() {
      const _recipients: recipient[] = convertToUON(
        this.$refs.comboRecipients.destinations
      );

      senderUid.resourceUid = this.$store.state.authModule?.user.userId;
      subject.resourceUid = this.subject;
      clientAppMsg.resourceUid = "68815";

      if (
        this.subject &&
        this.recipientOptions &&
        this.recipientOptions.length > 0 &&
        this.message &&
        this.$store.state.authModule?.user.userId
      ) {
        _recipients.forEach((el: any) => {
          delete el.recipient.read_daaq;
          delete el.recipient.email;
        });

        if (this.selectedReference) {
          if (this.selectedReference.length > 1) {
            const allHaveReferenceOrder = this.selectedReference.every(
              (ref: any) => ref.referenceorder !== undefined && ref.referenceorder !== null
            );
            if (allHaveReferenceOrder) {
              const referenceorders = this.selectedReference
                .map((ref: any) => ref.referenceorder)
                .join(",");
              return store
                .dispatch("messagingModule/messageSend", {
                  objectType: objectType_odmTransferReplyMsg,
                  systemUid,
                  daaq: `${store.state.authModule?.daaq}${store.state.authModule?.daaqResourceUid}/${store.state.authModule?.daaqLabel}`,
                  sender: `${store.state.authModule?.user.firstName} ${store.state.authModule?.user.lastName}`,
                  senderUid: senderUid,
                  priority: this.importantTag,
                  subject: subject,
                  type: typeMsg,
                  recipients: _recipients,
                  body: this.message,
                  relatedObjects: null,
                  clientApplication: clientAppMsg,
                  parameters: this.getParametersForMultipleOrder(referenceorders),
                  messageTemplate: messageTemplate,
                })
                .then(() => {
                  this.resetForm();
                  this.$emit("message-sent");
                });
            }
          }

          for (const ref of this.selectedReference) {
            store
              .dispatch("messagingModule/messageSend", {
                objectType: objectType_odmTransferReplyMsg,
                systemUid,
                daaq: `${store.state.authModule?.daaq}${store.state.authModule?.daaqResourceUid}/${store.state.authModule?.daaqLabel}`,
                sender: `${store.state.authModule?.user.firstName} ${store.state.authModule?.user.lastName}`,
                senderUid: senderUid,
                priority: this.importantTag,
                subject: subject,
                type: typeMsg,
                recipients: _recipients,
                body: this.message,
                relatedObjects: null,
                clientApplication: clientAppMsg,
                parameters: this.getParameters(ref),
                messageTemplate: messageTemplate,
              })
              .then(() => {
                this.resetForm();
                this.$emit("message-sent");
              });
          }
        } else {
          store
            .dispatch("messagingModule/messageSend", {
              objectType: objectType_odmTransferReplyMsg,
              systemUid,
              daaq: `${store.state.authModule?.daaq}${store.state.authModule?.daaqResourceUid}/${store.state.authModule?.daaqLabel}`,
              sender: `${store.state.authModule?.user.firstName} ${store.state.authModule?.user.lastName}`,
              senderUid: senderUid,
              priority: this.importantTag,
              subject: subject,
              type: typeMsg,
              recipients: _recipients,
              body: this.message,
              relatedObjects: null,
              clientApplication: clientAppMsg,
              messageTemplate: messageTemplate,
              parameters: {
                Context: null,
                Entity_Reference: null,
                Entity_Daaq: null,
                Entity_Status: null,
              },
            })
            .then(() => {
              this.resetForm();
              this.$emit("message-sent");
            });
        }
      }
    },

    getParameters(obj: any) {
      if (obj.referenceorder) {
        const entityId = store.getters["authModule/getSelectedUserNetwork"];
        return {
          Context: "ORDER",
          Entity_Reference: obj.referenceorder,
          Entity_Daaq: obj._daaq,
          Entity_Status: obj.status_code,
          Entity_Id: entityId.value,
        };
      } else {
        return {
          Context: `${obj.id ? "PARTY" : "OFFER"}`,
          Entity_Reference: obj.reference,
          Entity_Daaq: obj._daaq,
          Entity_Status: `${obj.statuscode ? obj.statuscode : obj.current_status_code}`,
        };
      }
    },

    getParametersForMultipleOrder(referenceorders: any) {
      const entityId = store.getters["authModule/getSelectedUserNetwork"];
      return {
        Context: "ORDER",
        Entity_Reference: referenceorders,
        Entity_Daaq: "/",
        Entity_Status: "ORDER",
        Entity_Id: entityId.value,
      };
    },

    resetForm() {
      this.importantTag = false;
      this.subject = null;
      this.selectedReference = null;
      this.message = null;
      if (this.$refs.comboRecipients) {
        this.$refs.comboRecipients.destinations = [];
      }
    },

    isRequired(val: string | any[]) {
      return (
        (val && val.length > 0) ||
        i18n.global.t("communication.comMessage.missingField")
      );
    },

    filterRefFn(val: string, update: any) {
      if (val === "" || val === " ") {
        this.referenceOptions = this.references;
        update(this.referenceOptions);
        return;
      }
      const limitedInterval = setInterval(() => {
        update(() => {
          this.referenceOptions = [];
          store.dispatch("demandModule/searchOfferPartyOrder", {
            callback: this.updateResultRows,
            offerFilter: {
              reference: `%${val}%`,
            },
            filterParty: {
              reference: `%${val}%`,
            },
            orderFilter: {
              externalReference: `%${val}%`,
            },
          });
        });
        clearInterval(limitedInterval);
      }, 2000);
      return limitedInterval;
    },

    updateResultRows(res: any) {
      this.referenceOptions = res;
    },

    abortRefFilter() {
      this.referenceOptions = this.references;
    },

    getSelectedLabel(scope: any) {
      if (scope.opt.id) {
        return {
          id: scope.opt.reference ? scope.opt.reference : scope.opt.id,
          context: "Party",
        };
      } else if (scope.opt.reference) {
        return {
          id: scope.opt.reference,
          context: "Offer",
        };
      } else if (scope.opt.externalreference) {
        return {
          id: scope.opt.externalreference,
          context: "Order",
        };
      }
    },
  },
})
export default class MessageTab extends Vue {
  // Props declarations
  subjects!: any[];
  references!: any[];
  reference!: any;
  recipients!: any[];

  // Local state
  importantTag = false;
  subject: any = null;
  subjectOptions: any[] = [];
  selectedReference: any = null;
  referenceOptions: any[] = [];
  recipientOptions: any[] = [];
  message: any = null;

  get isSending() {
    return store.state.messagingModule?.isSendingMessage;
  }
}
</script>

<style scoped>
.message-tab-container {
  padding: 10px 0;
}
</style>
